import { useEffect, useState } from "react";
import { useSearchParams, useNavigate, Link } from "react-router-dom";
import { volApi, formatVolError, storeVolSession } from "./volApi";
import { HarborWellFormHeader } from "@/components/HarborWellFormHeader";
import { Loader2, Check, AlertCircle, KeyRound } from "lucide-react";

/**
 * Volunteer magic-link invite acceptance page.
 *
 * Flow:
 *   1. Admin invites a volunteer → email arrives with `/volunteer/accept?token=...`
 *   2. Recipient lands here, sets a password (8+ chars, confirm match).
 *   3. POST /api/volunteers/accept-invite → status flips to active.
 *   4. We immediately log them in for a smooth first experience.
 */
export default function VolunteerAcceptInvite() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get("token") || "";

  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [done, setDone] = useState(false);
  const [accepted, setAccepted] = useState(null); // {email}

  useEffect(() => {
    if (!token) setError("This invite link is missing its token. Ask the admin for a fresh invite.");
  }, [token]);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (password.length < 8) { setError("Password must be at least 8 characters."); return; }
    if (password !== confirm) { setError("Passwords don't match."); return; }
    setBusy(true);
    try {
      const { data } = await volApi.post("/volunteers/accept-invite", { token, password });
      setAccepted(data);
      // Auto-login for a smooth first experience
      try {
        const { data: login } = await volApi.post("/volunteers/login", { email: data.email, password });
        storeVolSession(login.access_token, login.volunteer);
        setDone(true);
        setTimeout(() => navigate("/volunteer"), 1500);
      } catch {
        // If auto-login fails for any reason, just send them to the login page.
        setDone(true);
        setTimeout(() => navigate("/volunteer/login"), 1500);
      }
    } catch (err) {
      setError(formatVolError(err));
    } finally {
      setBusy(false);
    }
  };

  if (done) {
    return (
      <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
        <div className="max-w-md text-center bg-white border border-[#E5E5E0] rounded-2xl p-10" data-testid="volunteer-accept-success">
          <div className="w-14 h-14 mx-auto rounded-2xl bg-[#166534] text-white flex items-center justify-center">
            <Check className="w-7 h-7" strokeWidth={1.75} />
          </div>
          <h2 className="font-display text-2xl font-semibold mt-5">Welcome to HarborWell!</h2>
          <p className="text-[#334155] mt-3">
            Your volunteer account is active. Taking you to the portal…
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F8F6] flex items-center justify-center p-6">
      <div className="w-full max-w-md">
        <div className="mb-6">
          <HarborWellFormHeader
            eyebrow="Volunteer Portal"
            homeHref="/volunteer/login"
            size="md"
            testId="volunteer-accept-brand-header"
          />
        </div>

        <div className="bg-white border border-[#E5E5E0] rounded-2xl p-8">
          <div className="hw-eyebrow mb-2 flex items-center gap-1.5">
            <KeyRound className="w-3.5 h-3.5" /> Set your password
          </div>
          <h1 className="font-display text-2xl font-semibold text-[#0F172A] mb-2">Activate your account</h1>
          <p className="text-sm text-[#334155] mb-6">
            You&apos;ve been invited to volunteer with HarborWell. Choose a password to finish setting up.
          </p>

          {!token ? (
            <div className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700 flex items-start gap-2" data-testid="volunteer-accept-missing-token">
              <AlertCircle className="w-4 h-4 mt-0.5 flex-shrink-0" />
              This invite link is missing its token. Ask the admin to re-send the invite.
            </div>
          ) : (
            <form onSubmit={submit} className="space-y-4" data-testid="volunteer-accept-form">
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">Choose a password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  data-testid="volunteer-accept-password"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
                  placeholder="At least 8 characters"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-[#334155] mb-2">Confirm password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  autoComplete="new-password"
                  value={confirm}
                  onChange={(e) => setConfirm(e.target.value)}
                  data-testid="volunteer-accept-confirm"
                  className="w-full px-4 py-3 rounded-xl bg-white border border-[#E5E5E0] focus:outline-none focus:ring-2 focus:ring-[#7E937D] text-[#0F172A]"
                />
              </div>
              {error && (
                <div data-testid="volunteer-accept-error" className="px-4 py-3 rounded-xl bg-red-50 border border-red-200 text-sm text-red-700">
                  {error}
                </div>
              )}
              <button
                type="submit"
                disabled={busy}
                data-testid="volunteer-accept-submit"
                className="w-full py-3 rounded-full bg-[#1E3A8A] text-white font-medium hover:bg-[#172554] transition-colors disabled:opacity-60 flex items-center justify-center gap-2"
              >
                {busy && <Loader2 className="w-4 h-4 animate-spin" />}
                Activate account & sign in
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
